import { Link, NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "./LanguageSwitcher";
import { Menu, X, Instagram } from "lucide-react";
import { useState } from "react";

// FontAwesome WhatsApp icon (SVG) — lucide-react has no dedicated WhatsApp icon.
const WhatsAppIcon = ({ size = 20 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M20.52 3.48A11.86 11.86 0 0 0 12.06 0C5.53 0 .23 5.3.23 11.83c0 2.08.55 4.11 1.6 5.9L0 24l6.42-1.69a11.83 11.83 0 0 0 5.64 1.44h.01c6.53 0 11.83-5.3 11.83-11.83 0-3.16-1.23-6.13-3.38-8.44Zm-8.46 18.2h-.01a9.83 9.83 0 0 1-5.01-1.37l-.36-.21-3.81 1 1.02-3.71-.23-.38a9.82 9.82 0 0 1-1.5-5.18c0-5.43 4.42-9.85 9.85-9.85 2.63 0 5.1 1.03 6.96 2.89a9.79 9.79 0 0 1 2.88 6.96c0 5.43-4.42 9.85-9.79 9.85Zm5.4-7.38c-.3-.15-1.75-.86-2.02-.96-.27-.1-.47-.15-.66.15-.2.3-.76.96-.94 1.16-.17.2-.35.22-.64.07-.3-.15-1.24-.46-2.37-1.46-.87-.78-1.46-1.73-1.63-2.03-.17-.3-.02-.46.13-.6.13-.13.3-.35.44-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.66-1.6-.9-2.19-.24-.58-.48-.5-.66-.51h-.56c-.2 0-.52.07-.79.37-.27.3-1.03 1.01-1.03 2.46s1.05 2.85 1.2 3.05c.15.2 2.08 3.17 5.03 4.44.7.3 1.25.48 1.68.62.7.22 1.34.19 1.85.11.56-.08 1.75-.71 2-1.4.24-.68.24-1.27.17-1.4-.07-.13-.27-.2-.57-.35Z" />
  </svg>
);

const INSTAGRAM_URL = "https://www.instagram.com/planit_studio/";
const WHATSAPP_URL = "https://wa.me/390743050842";

export default function Nav() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const links = [
    { to: "/", label: t("nav.home"), end: true },
    { to: "/teatro-dinamico", label: t("nav.teatro") },
    { to: "/servizi", label: t("nav.services") },
    { to: "/chi-siamo", label: t("nav.about") },
    { to: "/contatti", label: t("nav.contact") },
  ];

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#F9F8F6]/85 border-b border-stone-200">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center">
          <img
            src="/logo.png"
            alt="Plan It Studio"
            className="h-14 w-auto object-contain"
          />
        </Link>
        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`nav-${l.to === "/" ? "home" : l.to.replace("/", "")}`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors ${
                  isActive ? "text-[#D06D4D]" : "text-stone-700 hover:text-stone-900"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden md:flex items-center gap-5">
          <LanguageSwitcher />
          <a
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram"
            data-testid="nav-instagram"
            className="text-stone-700 hover:text-[#D06D4D] transition-colors"
          >
            <Instagram size={20} />
          </a>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="WhatsApp"
            data-testid="nav-whatsapp"
            className="text-stone-700 hover:text-[#25D366] transition-colors"
          >
            <WhatsAppIcon size={20} />
          </a>
        </div>
        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden p-2 text-stone-900"
          onClick={() => setOpen(!open)}
          aria-label="menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      {open && (
        <div className="md:hidden border-t border-stone-200 bg-white px-6 py-6 flex flex-col gap-4">
          {links.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="text-stone-800 font-medium">
              {l.label}
            </Link>
          ))}
          <div className="pt-2 flex items-center gap-5">
            <LanguageSwitcher />
            <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="text-stone-700 hover:text-[#D06D4D]"><Instagram size={20} /></a>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" className="text-stone-700 hover:text-[#25D366]"><WhatsAppIcon size={20} /></a>
          </div>
        </div>
      )}
    </header>
  );
}
